/**
 * Pack Découverte — Helpers réutilisables
 * ============================================================
 * Ce fichier contient les fonctions pures (sans DOM, sans Shopify)
 * extraites de packdecouverteverlou.js pour pouvoir être :
 *   • testées unitairement (Node)
 *   • réutilisées dans d'autres scripts du thème
 *
 * Compatible browser (window.PackDecouverteHelpers) ET Node (require).
 * À charger AVANT packdecouverteverlou.js dans le template Liquid.
 * ============================================================
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    // Node / CommonJS — pour les tests unitaires
    module.exports = factory();
  } else {
    // Browser — expose sur window
    root.PackDecouverteHelpers = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /**
   * Calcule le couple (original, discounted, savingsPct) pour un prix.
   *
   * Sources de la remise (par ordre de priorité) :
   *  1) Si compare_at_price > price → on respecte ce que Shopify dit
   *     (le marchand a configuré sa remise dans Shopify Admin).
   *  2) Sinon → on applique le pourcentage de remise configuré.
   *
   * Robustesse :
   *  • Garde-fou compare > price (pas seulement > 0) — évite l'inversion
   *    si le marchand a saisi par erreur un compare_at_price plus bas.
   *  • Garde-fou contre la division par 0 (variant gratuit).
   *  • savingsPct toujours dans [0, 99].
   *  • Gère les inputs invalides (null, undefined, NaN, négatifs, strings).
   *
   * @param {number|string} rawPrice    - prix Shopify en centimes
   * @param {number|string} rawCompare  - compare_at_price en centimes (0/null = absent)
   * @param {number|string} discountPct - remise par défaut si pas de compare_at_price
   * @returns {{original:number, discounted:number, savingsPct:number}}
   */
  function computePrice(rawPrice, rawCompare, discountPct) {
    const price   = Number.isFinite(+rawPrice)   ? Math.max(0, +rawPrice)   : 0;
    const compare = Number.isFinite(+rawCompare) ? Math.max(0, +rawCompare) : 0;
    const pct     = Number.isFinite(+discountPct)
      ? Math.min(99, Math.max(0, +discountPct))
      : 0;

    let original, discounted;
    // On considère le compare_at_price seulement s'il est strictement supérieur
    // au prix (sinon ce n'est pas une remise — possiblement une erreur de saisie).
    if (compare > price) {
      original   = compare;
      discounted = price;
    } else {
      original   = price;
      discounted = Math.round(price * (1 - pct / 100));
    }

    // Garde-fou division par 0
    const savingsPct = original > 0
      ? Math.max(0, Math.min(99, Math.round((1 - discounted / original) * 100)))
      : 0;

    return { original, discounted, savingsPct };
  }

  /**
   * Traduit les messages d'erreur Shopify en français.
   * Retourne null si aucune correspondance (le caller utilisera le message brut).
   *
   * @param {string} rawMessage - message d'erreur brut depuis Shopify
   * @param {number} status     - code HTTP
   * @returns {string|null}
   */
  function translateShopifyError(rawMessage, status) {
    if (!rawMessage && !status) return null;
    const msg = String(rawMessage || '').toLowerCase();

    // Stock épuisé / variant supprimé
    if (
      msg.includes('no longer available') ||
      msg.includes('sold out') ||
      msg.includes('stock')
    ) {
      return "Un des produits n'est plus disponible. Veuillez recharger la page pour voir les stocks à jour.";
    }

    // Quantité dépassée
    if (
      msg.includes('exceeds available quantity') ||
      msg.includes('not enough') ||
      msg.includes('insufficient')
    ) {
      return "Stock insuffisant pour un des produits sélectionnés.";
    }

    // Variante introuvable
    if (msg.includes('variant') && (msg.includes('not found') || msg.includes('does not exist'))) {
      return "Une des variantes n'existe plus. Veuillez recharger la page.";
    }

    // Panier saturé
    if (msg.includes('cart') && msg.includes('full')) {
      return "Le panier est plein. Veuillez vider votre panier avant d'ajouter ce pack.";
    }

    // Erreurs HTTP génériques
    if (status === 404) return "Produit introuvable.";
    if (status === 422) return "Cette combinaison n'est pas valide. Veuillez réessayer.";
    if (status >= 500) return "Erreur du serveur Shopify. Veuillez réessayer dans quelques instants.";

    return null;
  }

  /**
   * Détecte et tente d'ouvrir le tiroir panier du thème Shopify courant.
   * Compatible avec : Dawn, Impulse, Symmetry, Prestige, Palo Alto,
   * et autres thèmes exposant des sélecteurs/événements standards.
   *
   * @returns {boolean} true si un drawer a été détecté et ouvert
   */
  function openThemeCartDrawer() {
    // 1. Dawn (thème officiel Shopify) : custom element <cart-drawer>
    const dawnDrawer = document.querySelector('cart-drawer');
    if (dawnDrawer && typeof dawnDrawer.open === 'function') {
      try { dawnDrawer.open(); return true; } catch (_) {}
    }

    // 2. Palo Alto (Fluorescent Design) — objet JS exposé sur window
    if (window.theme && window.theme.cartDrawer && typeof window.theme.cartDrawer.open === 'function') {
      try { window.theme.cartDrawer.open(); return true; } catch (_) {}
    }

    // 3. Selectors courants : data-attributes / classes connues
    //    Palo Alto : [data-js="cart-toggle"], [aria-controls="cart-drawer"]
    //    Impulse   : [data-drawer-toggle="cart-drawer"]
    //    Symmetry  : .js-drawer-open-cart
    //    Expanse   : [data-cart-drawer-toggle]
    const selectors = [
      '[data-js="cart-toggle"]',            // Palo Alto
      '[aria-controls="cart-drawer"]',      // Palo Alto / generic
      '[aria-controls="CartDrawer"]',       // Palo Alto variant
      '[data-open="cart-drawer"]',          // Palo Alto
      '[data-cart-drawer-toggle]',          // Expanse / Symmetry
      '[data-action="open-drawer"][data-drawer="cart"]', // Impulse
      '[data-drawer-toggle="cart-drawer"]', // Impulse
      '.js-drawer-open-cart',               // Symmetry / older
      '.cart-drawer-toggle',
      '#cart-icon-bubble',                  // Dawn fallback
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) {
        try { el.click(); return true; } catch (_) {}
      }
    }

    // 4. Événements custom communs
    //    Palo Alto dispatche 'on:cart:open' sur document
    document.dispatchEvent(new CustomEvent('on:cart:open'));
    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));

    return false;
  }

  return {
    computePrice,
    translateShopifyError,
    openThemeCartDrawer,
  };
});